import {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {selectTransaction} from '@components/SignerUi/signerUiSlice';
import {selectAccessToken} from '@components/Login/userSlice';
import {parseJWT} from '@utils/jwtUtils';
import {AUTH_USER_ROLE} from '@constants/user';

interface FaceToFaceRoleResult {
    isFaceToFace: boolean;
    isFaceToFaceHost: boolean;
    isFaceToFaceRecipient: boolean;
    isLoading: boolean;
}

/**
 * Determines the current user's role in a face-to-face (in-person) signing session.
 *
 * @returns {FaceToFaceRoleResult} Object containing:
 *   - isFaceToFace: whether the transaction is a face-to-face session
 *   - isFaceToFaceHost: whether the current user is the F2F host (owner with 'user' role)
 *   - isFaceToFaceRecipient: whether the current user is a F2F recipient (non-host in F2F session)
 */
export function useFaceToFaceRole(): FaceToFaceRoleResult {
    const transaction = useSelector(selectTransaction);
    const accessToken = useSelector(selectAccessToken);

    return useMemo(() => {
        const isLoading = !transaction || !accessToken;

        if (isLoading) {
            return {
                isFaceToFace: false,
                isFaceToFaceHost: false,
                isFaceToFaceRecipient: false,
                isLoading: true
            };
        }

        const faceToFaceSetting = transaction?.settings?.face_to_face;
        const isFaceToFace = faceToFaceSetting === true || faceToFaceSetting === 'true';

        if (!isFaceToFace) {
            return {
                isFaceToFace: false,
                isFaceToFaceHost: false,
                isFaceToFaceRecipient: false,
                isLoading: false
            };
        }

        const tokenPayload = parseJWT(accessToken);
        const isUserRole = tokenPayload?.role === AUTH_USER_ROLE;
        const isOwner = tokenPayload?.external_id === transaction?.owner_id;

        const F2F_EXCHANGE_REASON = 'face-to-face';

        const isFaceToFaceHost = isUserRole && isOwner;
        const hasF2FExchangeReason = tokenPayload?.exchange_reason === F2F_EXCHANGE_REASON;
        const isFaceToFaceRecipient = isFaceToFace && !isUserRole && hasF2FExchangeReason;

        return {
            isFaceToFace,
            isFaceToFaceHost,
            isFaceToFaceRecipient,
            isLoading: false
        };
    }, [transaction, accessToken]);
}

export default useFaceToFaceRole;
